/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './biggest-movers.stache';
import DomEventsManager from 'scripts/dom-events-manager';
import ViewModel from './biggest-movers.model';
import FeedService from 'scripts/services/base-feed-service';
import {
    getFieldName,
    MAKE_CUT_PROBABILITY,
    TOP_10_PROBABILITY,
    WIN_PROBABILITY,
} from 'components/probability/types';
import can from 'can';

Component.extend({
    tag: 'biggest-movers',
    template: template,
    viewModel: ViewModel,
    events: {
        inserted() {
            this.fetchFeed();
            DomEventsManager.subscribe(this);
            this.updateProbabilityTypes();

            this.viewModel.bind('round', () => {
                this.updateProbabilityTypes();
            });

            this.navListener = (event) => {
                const nav = document.getElementById('biggest-movers-nav');

                if (nav && !nav.contains(event.target)) {
                    this.viewModel.attr('isNavOpen', false);
                }
            };

            document.addEventListener('mousedown', this.navListener);
        },
        changeProbabilityType(probabilityType) {
            this.attr('isNavOpen', false);
            this.dispatch('probabilityChange', [probabilityType]);
        },
        updateProbabilityTypes() {
            const round = this.viewModel.attr('round');
            const probabilityType = this.viewModel.attr('probabilityType');
            const baseTypes = [
                MAKE_CUT_PROBABILITY,
                WIN_PROBABILITY,
                TOP_10_PROBABILITY,
            ];
            const mapTypes = (type) => ({
                type,
                active: probabilityType === type,
            });

            if (round === 0) {
                this.viewModel.attr(
                    'probabilityTypes',
                    baseTypes.map(mapTypes),
                );
                return;
            }

            const types = baseTypes
                .filter((type) => type !== MAKE_CUT_PROBABILITY || round <= 2)
                .map(mapTypes);

            this.viewModel.attr('probabilityTypes', types);
        },
        fetchFeed: function() {
            const biggestMoversFeedUrl =
                this.options.biggestMoversFeedUrl ||
                window.globalSettings.feeds.biggestMoversUrl;

            const compute = can.compute(() => ({
                makeCutRounds: this.viewModel.attr('makeCut'),
                top10Rounds: this.viewModel.attr('top10'),
                winProbabilityRounds: this.viewModel.attr('win'),
                probabilityType: this.viewModel.attr('probabilityType'),
            }));

            compute.bind('change', (__, vm) => {
                if (!vm.probabilityType) {
                    const firstCameData =
                        vm.makeCutRounds ||
                        vm.winProbabilityRounds ||
                        vm.top10Rounds;

                    this.viewModel.attr(
                        'probabilityType',
                        firstCameData.currentRound > 2
                            ? WIN_PROBABILITY
                            : MAKE_CUT_PROBABILITY,
                    );

                    return;
                }

                const data = vm[getFieldName(vm.probabilityType)];

                if (!data) {
                    return;
                }

                this.viewModel.attr('standings', data.players);
                this.viewModel.attr('round', data.currentRound);

                if (!this.viewModel.attr('activeId')) {
                    this.viewModel.attr(
                        'activeId',
                        data.players[0].player.identifier,
                    );
                }

                // dirty hack to make swiper at normal size
                setTimeout(() => {
                    this.viewModel.attr('standings', data.players);
                    this.viewModel.attr('round', data.currentRound);
                }, 3000);
            });

            this.makeCutFeed = new FeedService({
                feedUrl: can.sub(biggestMoversFeedUrl, {
                    filter: 'diffmakeCut',
                }),
                fromCache: false,
                autoUpdate: true,
            });
            this.top10Feed = new FeedService({
                feedUrl: can.sub(biggestMoversFeedUrl, { filter: 'difftop10' }),
                fromCache: false,
                autoUpdate: true,
            });
            this.winProbabilityFeed = new FeedService({
                feedUrl: can.sub(biggestMoversFeedUrl, { filter: 'diffwin' }),
                fromCache: false,
                autoUpdate: true,
            });
            this.makeCutFeed.on('data', (ev, data) => {
                this.viewModel.attr('makeCut', data);
            });
            this.winProbabilityFeed
                .on('fetching', (ev, value) =>
                    this.viewModel.attr('fetching', value),
                )
                .on('data', (ev, data) => {
                    this.viewModel.attr('win', data);
                });
            this.top10Feed.on('data', (ev, data) => {
                this.viewModel.attr('top10', data);
            });
            this.makeCutFeed.fetch();
            this.top10Feed.fetch();
            this.winProbabilityFeed.fetch();
        },
        destroy() {
            document.removeEventListener('mousedown', this.navListener);
        },
    },
});
